import React, { useState, useRef, useEffect } from "react";
import { useMdmRunStore } from "../store/useMdmRunStore";

import type {
  MdmMatchRule,
  MdmBlockingConfig,
  MdmConfidenceBands,
  MdmSurvivorshipRule,
  MdmForceHitlRule,
  MdmMatchConfigSuggestion,
  BlockingStrategy,
  MatchMethod,
  SurvivorshipStrategy,
  OverrideOperator,
  MdmCompositeRule,
  MdmRuleLine,
  EntityType,
} from "../types";

import {
  suggestMatchConfig,
  saveMatchConfig,
  executeRun,
  resetRun,
  getRunStatus,
  getFields,
  applyEntityTemplate,
  exportMatchConfig,
  validateImportedConfig,
  estimateBlocking,
} from "../api/mdm-client";

// ---------------------------------------------------------------------------
// Prop & state types
// ---------------------------------------------------------------------------

export interface MdmRuleBuilderViewProps {
  runId: string;
  projectId: string;
  canonicalFields: string[];
  entityType?: EntityType | null;
  onTestRun: () => void;
}

type TabId =
  | "blocking"
  | "match_rules"
  | "field_weights"
  | "confidence"
  | "survivorship"
  | "force_hitl";

interface VersionEntry {
  id: string;
  name: string;
  version: string;
  created_at: string;
  run_count: number;
}

// ---------------------------------------------------------------------------
// Helpers & constants
// ---------------------------------------------------------------------------

/**
 * Canonical field names for each MDM entity type. Used to pre-populate
 * blocking keys, match rules, and survivorship rules when the entity type is
 * known (e.g. from "Apply Entity Template"). These are the Deloitte GPS-standard
 * canonical names — the backend's own template endpoint validates and returns
 * any that are actually present in the connected source.
 */
export const ENTITY_CANONICAL_FIELDS: Record<string, string[]> = {
  person: [
    "first_name",
    "last_name",
    "full_name",
    "date_of_birth",
    "gender",
    "address_line1",
    "city",
    "state",
    "zip",
    "phone",
    "email",
    "ssn_last4",
  ],
  organization: [
    "org_name",
    "legal_name",
    "dba_name",
    "ein",
    "duns",
    "address_line1",
    "city",
    "state",
    "zip",
    "phone",
    "website",
    "naics_code",
  ],
  provider: [
    "provider_name",
    "first_name",
    "last_name",
    "npi",
    "license_number",
    "specialty",
    "taxonomy_code",
    "address_line1",
    "city",
    "state",
    "zip",
    "phone",
  ],
  facility: [
    "facility_name",
    "dba_name",
    "ccn",
    "npi",
    "license_number",
    "address_line1",
    "city",
    "state",
    "zip",
    "phone",
    "fax",
    "facility_type",
  ],
};

const BLOCKING_STRATEGIES: { value: BlockingStrategy; label: string }[] = [
  { value: "exact", label: "Exact" },
  { value: "n_gram", label: "N-Gram" },
  { value: "phonetic", label: "Phonetic" },
  { value: "sorted_token", label: "Sorted Token" },
  { value: "lsh", label: "LSH (MinHash)" },
];

const MATCH_METHODS: { value: MatchMethod; label: string }[] = [
  { value: "exact", label: "Exact" },
  { value: "jaro_winkler", label: "Jaro-Winkler" },
  { value: "levenshtein", label: "Levenshtein" },
  { value: "soundex", label: "Soundex" },
  { value: "metaphone", label: "Metaphone" },
  { value: "token_set_ratio", label: "Token Set Ratio" },
  { value: "date_normalized", label: "Date Normalized" },
  { value: "address_standardized", label: "Address Std." },
];

const SURVIVORSHIP_STRATEGIES: {
  value: SurvivorshipStrategy;
  label: string;
  llm?: boolean;
}[] = [
  { value: "most_recent", label: "Most Recent" },
  { value: "most_complete", label: "Most Complete" },
  { value: "source_priority", label: "Source Priority" },
  { value: "most_frequent", label: "Most Frequent" },
  { value: "non_null", label: "Non-Null" },
  { value: "llm_assisted", label: "LLM-Assisted", llm: true },
];

const OVERRIDE_OPERATORS: { value: OverrideOperator; label: string }[] = [
  { value: "eq", label: "= equals" },
  { value: "neq", label: "≠ not equals" },
  { value: "is_true", label: "is true" },
  { value: "in", label: "in list" },
];

// Pick a comparator from a field's name semantics so a zip/date/id isn't
// fuzzy-matched like a name. Restricted to the three backend-proven methods.
function comparatorFor(field: string): MatchMethod {
  const f = field.toLowerCase();
  if (
    /(dob|birth)/.test(f) ||
    (/(date|_dt$|dtm)/.test(f) && !/(creat|updat|replicat)/.test(f))
  )
    return "date_normalized";
  if (
    /email|phone|zip|postal|ssn|nbr|code|status|type|state|county|region|_id$|(^|_)id($|_)|(^|_)(npi|license|dea|duns|tin|ein)($|_)/.test(
      f,
    )
  )
    return "exact";
  return "jaro_winkler"; // names, addresses, general text
}

// ETL/row-lifecycle metadata (when a row was created/updated/loaded in THIS
// system), not an attribute of the real-world entity — matching on it tells
// you nothing about whether two records are the same provider/person/org, so
// it shouldn't be a candidate match/grouping field at all. This deliberately
// mirrors the (creat|updat|replicat) exclusion comparatorFor already uses to
// keep these out of the date_normalized bucket — that only stopped them from
// being compared AS dates, not from being compared at all.
function isAuditMetadataField(field: string): boolean {
  const f = field.toLowerCase();
  return (
    /(creat|updat|replicat|insert|delet|load|etl|audit|row_)/.test(f) &&
    /(date|_dt$|dtm|_at$|_ts$|timestamp)/.test(f)
  );
}

// Bare geography (city/county/state/region/zip) is a weak standalone identity
// signal — many unrelated entities legitimately share the same city or state,
// so an agreement here is common by chance, not rare. address_line1 is left
// out on purpose: a full street address is far more specific than a bare
// city/state and doesn't share this weakness.
function isLocationLikeField(field: string): boolean {
  return /(^|_)(city|county|state|region|zip|postal)(_|$)/.test(
    field.toLowerCase(),
  );
}

function defaultMatchRules(fields: string[]): MdmMatchRule[] {
  return fields
    .filter((f) => !isAuditMetadataField(f))
    .map((f) => {
      const method = comparatorFor(f);
      const location = isLocationLikeField(f);
      return {
        field: f,
        method,
        weight: location ? 0.5 : 1.0,
        m_probability: location ? 0.7 : 0.9,
        u_probability: location ? 0.4 : 0.1,
        threshold: method === "jaro_winkler" ? 80 : 100,
      };
    });
}

// Build composite matching rules from the DISCOVERED fields — no hardcoded
// person schema. Each rule pairs a name-like field with a corroborating anchor
// (geography / contact / license), uses a comparator that fits each field, and
// is LABELED by the fields it actually uses (so "provider_name + zip", never a
// phantom "Name + DOB + ZIP" on address_line1).
export function defaultCompositeRules(allFields: string[]): MdmCompositeRule[] {
  // Row-lifecycle metadata (created_dtm etc.) isn't an entity attribute —
  // never let it become a match/anchor field. See isAuditMetadataField.
  const fields = allFields.filter((f) => !isAuditMetadataField(f));
  if (!fields.length) return [];
  const low = (f: string) => f.toLowerCase();
  const match = (re: RegExp, extraExclude?: RegExp) =>
    fields.filter(
      (f) => re.test(low(f)) && !(extraExclude?.test(low(f)) ?? false),
    );

  const names = match(/name/, /user|system|creat|updat|replicat/);
  const zips = match(/zip|postal/);
  const addresses = match(/address|street|addr/);
  const geos = match(/city|county|region|state/);
  const emails = match(/email/);
  const phones = match(/phone/);
  const licenses = match(
    /license|permit|npi|registration/,
    /status|date|type|dt/,
  );
  const dates = match(/dob|birth/).concat(
    match(/date|_dt$|dtm/, /creat|updat|replicat/),
  );

  const nameF = names[0];
  const rules: MdmCompositeRule[] = [];
  const addRule = (conds: MdmCompositeRule["conditions"]) => {
    if (conds.length < 2) return;
    rules.push({
      id: uid(),
      name: `Rule ${rules.length + 1} — ${conds.map((c) => c.field).join(" + ")}`,
      enabled: true,
      priority: rules.length + 1,
      conditions: conds,
    });
  };
  const cond = (field: string, threshold?: number) => {
    const method = comparatorFor(field);
    return {
      field,
      method,
      threshold: threshold ?? (method === "jaro_winkler" ? 85 : 100),
    };
  };

  if (nameF) {
    const geoAnchor = zips[0] ?? addresses[0] ?? geos[0];
    if (geoAnchor) addRule([cond(nameF), cond(geoAnchor)]);
    const contact = emails[0] ?? phones[0];
    if (contact) addRule([cond(nameF, 90), cond(contact)]);
    if (licenses[0]) addRule([cond(nameF), cond(licenses[0])]);
    if (dates[0]) addRule([cond(nameF), cond(dates[0])]);
  }

  // Fallback when no name-like field exists: pair the first two non-id text
  // fields so there's still a sensible starting rule the user can adjust.
  if (!rules.length) {
    const isId = (f: string) =>
      /(^|_)(id|nbr|number|uuid|guid|pk)($|_)|_id$/i.test(f);
    const textish = fields.filter(
      (f) => !isId(f) && comparatorFor(f) === "jaro_winkler",
    );
    const chosen = (textish.length >= 2 ? textish : fields).slice(0, 2);
    addRule(chosen.map((f) => cond(f)));
  }
  return rules;
}

function defaultSurvivorshipRules(fields: string[]): MdmSurvivorshipRule[] {
  return fields.map((f) => ({ field: f, strategy: "most_recent" }));
}

// Pick sensible default grouping (blocking) keys from whatever fields the
// source actually has. Grouping keys should CLUSTER likely-duplicates, so
// prefer name/location fields and avoid unique-id-like columns (a unique id as
// a grouping key puts every record in its own block → zero candidate pairs →
// zero merges, which is exactly what the hardcoded person schema caused on the
// provider table).
function pickDefaultBlockingKeys(fields: string[]): string[] {
  const isIdLike = (f: string) =>
    /(^|_)(id|nbr|number|uuid|guid|pk)($|_)/i.test(f) ||
    /_id$/i.test(f) ||
    /(license|ssn|npi|ein|tin|dea|duns)/i.test(f);
  const rank = (f: string): number => {
    const s = f.toLowerCase();
    if (s.includes("name")) return 5;
    if (/(city|zip|postal|county|region|state|address)/.test(s)) return 4;
    if (/(email|phone)/.test(s)) return 3;
    if (/(dob|birth|date)/.test(s)) return 2;
    return 1;
  };
  const groupable = fields.filter((f) => !isIdLike(f));
  const picks = [...groupable].sort((a, b) => rank(b) - rank(a)).slice(0, 2);
  return picks.length ? picks : fields.slice(0, 2);
}

/** Mirrors the backend's own default heuristic
 * (mdm/agent/engine/default_config.py `_default_blocking`): an exact match
 * on a name-like field (e.g. first_name) groups every same-named person
 * into one block — for a large population that's either skipped entirely
 * by the blocking engine's oversized-block cap, or, across many
 * medium-sized name groups that each stay just under that cap, can still
 * sum to a runaway candidate-pair count (confirmed live: "exact" on a
 * first-name field stalled a 104k-row Person run's blocking stage).
 * Phonetic clustering is the backend's own safer default for name-shaped
 * fields; exact stays the default for everything else. pickDefaultBlockingKeys
 * already ranks name fields highest, so without this the two defaults
 * combined guarantee picking the worst-case strategy for the field most
 * likely to be selected. */
function recommendBlockingStrategy(keys: string[]): BlockingStrategy[] {
  const hasNameField = keys.some((k) => k.toLowerCase().includes("name"));
  return hasNameField ? ["phonetic"] : ["exact"];
}

function uid(): string {
  return Math.random().toString(36).slice(2, 9);
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

// Glassmorphic card container
function GlassCard({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={`rounded-xl border border-[var(--border-muted)] bg-[var(--overlay-subtle)] backdrop-blur-sm dark:border-[var(--border-muted)] dark:bg-[var(--overlay-subtle)] light-card ${className}`}
    >
      {children}
    </div>
  );
}

// Badge pill
function Badge({
  children,
  variant = "default",
}: {
  children: React.ReactNode;
  variant?: "default" | "amber" | "emerald" | "rose" | "indigo";
}) {
  const colors: Record<string, string> = {
    default: "ztd-badge",
    amber: "ztd-badge ztd-badge-warning",
    emerald: "ztd-badge ztd-badge-success",
    rose: "ztd-badge ztd-badge-critical",
    indigo: "ztd-badge ztd-badge-ai",
  };
  return <span className={`${colors[variant]} tracking-wide`}>{children}</span>;
}

// Shared select style
const selectCls =
  "rounded-lg border border-[var(--border-muted)] bg-surface-200 px-2 py-1 text-sm text-slate-200 focus:outline-none focus:ring-2 focus:ring-brand-500/60 disabled:opacity-40 select-light";

// Shared input style
const inputCls =
  "rounded-lg border border-[var(--border-muted)] bg-surface-200 px-2 py-1 text-sm text-slate-200 focus:outline-none focus:ring-2 focus:ring-brand-500/60 input-light";

// Primary button
function Btn({
  children,
  onClick,
  disabled,
  variant = "primary",
  size = "md",
  type = "button",
  ...rest
}: {
  children: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  variant?: "primary" | "ghost" | "danger" | "success";
  size?: "sm" | "md" | "lg";
  type?: "button" | "submit";
} & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  const sizes = {
    sm: "ztd-btn-sm",
    md: "",
    lg: "text-sm px-5",
  };
  const variants: Record<string, string> = {
    primary: "ztd-btn-primary",
    ghost: "ztd-btn-ghost",
    danger: "border border-error/30 bg-error/20 text-error hover:bg-error/30",
    success:
      "border border-success/30 bg-success/20 text-success hover:bg-success/30",
  };
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`ztd-btn ${sizes[size]} ${variants[variant]}`}
      {...rest}
    >
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Tab: Blocking
// ---------------------------------------------------------------------------

interface BlockingTabProps {
  fields: string[];
  config: MdmBlockingConfig;
  onChange: (c: MdmBlockingConfig) => void;
  runId?: string;
}

function BlockingTab({ fields, config, onChange, runId }: BlockingTabProps) {
  const [loading, setLoading] = useState(false);

  function toggleStrategy(s: BlockingStrategy) {
    const next = config.strategy.includes(s)
      ? config.strategy.filter((x) => x !== s)
      : [...config.strategy, s];
    onChange({ ...config, strategy: next });
  }

  function toggleKey(f: string) {
    const next = config.blocking_keys.includes(f)
      ? config.blocking_keys.filter((x) => x !== f)
      : [...config.blocking_keys, f];
    onChange({ ...config, blocking_keys: next });
  }

  async function estimateRecall() {
    setLoading(true);
    try {
      if (runId && config.blocking_keys.length > 0) {
        const strategies = config.strategy.map((s) => ({
          strategy: s,
          fields: config.blocking_keys,
        }));
        const result = await estimateBlocking(runId, strategies);
        onChange({
          ...config,
          recall_estimate: result.coverage_pct,
          candidate_pair_count: result.candidate_pair_count,
        });
      } else {
        // Fallback simulation when no runId or no keys selected
        await new Promise((r) => setTimeout(r, 1200));
        onChange({
          ...config,
          recall_estimate: 97.4,
          candidate_pair_count: Math.floor(Math.random() * 80000) + 20000,
        });
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="space-y-6 p-6">
      {/* Strategy picker */}
      <GlassCard className="p-5">
        <p className="mb-3 text-xs font-semibold uppercase tracking-widest text-[var(--text-primary)]">
          Grouping Strategy
        </p>
        <div className="flex flex-wrap gap-2">
          {BLOCKING_STRATEGIES.map((s) => {
            const active = config.strategy.includes(s.value);
            return (
              <button
                key={s.value}
                data-testid="mdm-blocking-strategy"
                data-value={s.value}
                onClick={() => toggleStrategy(s.value)}
                className={`rounded-lg border px-4 py-2 text-sm font-medium transition-all focus:outline-none focus:ring-2 focus:ring-brand-500 ${
                  active
                    ? "border-brand-500/60 bg-brand-500/20 text-brand-500"
                    : "border-[var(--border-muted)] bg-[var(--overlay-subtle)] text-[var(--text-primary)] hover:border-[var(--border-strong)] hover:text-slate-200"
                }`}
              >
                {s.label}
              </button>
            );
          })}
        </div>
        <p className="mt-3 text-xs text-[var(--text-primary)]">
          Select one or more strategies. N-Gram + Phonetic improves recall for
          dirty name data.
        </p>
      </GlassCard>

      {/* Blocking key selector */}
      <GlassCard className="p-5">
        <p className="mb-3 text-xs font-semibold uppercase tracking-widest text-[var(--text-primary)]">
          Grouping Keys
        </p>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
          {fields.map((f) => {
            const active = config.blocking_keys.includes(f);
            return (
              <label
                key={f}
                className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-all ${
                  active
                    ? "border-brand-500/40 bg-brand-500/10 text-slate-200"
                    : "border-[var(--border-muted)] bg-[var(--overlay-faint)] text-[var(--text-primary)] hover:border-[var(--border-strong)]"
                }`}
              >
                <input
                  type="checkbox"
                  data-testid="mdm-blocking-key"
                  data-field={f}
                  checked={active}
                  onChange={() => toggleKey(f)}
                  className="h-3.5 w-3.5 rounded border-[var(--border-strong)] bg-transparent accent-brand-500"
                />
                <span className="truncate font-mono text-xs">{f}</span>
              </label>
            );
          })}
        </div>
      </GlassCard>

      {/* Estimate recall */}
      <div className="flex items-start gap-4">
        <Btn
          onClick={estimateRecall}
          disabled={loading || config.blocking_keys.length === 0}
          data-testid="mdm-blocking-estimate-recall"
        >
          {loading ? (
            <>
              <svg
                className="h-3.5 w-3.5 animate-spin"
                viewBox="0 0 24 24"
                fill="none"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v8H4z"
                />
              </svg>
              Estimating…
            </>
          ) : (
            "Estimate Recall"
          )}
        </Btn>

        {config.recall_estimate !== undefined && (
          <GlassCard className="flex items-center gap-6 px-5 py-3">
            <div className="text-center">
              <p
                className="font-mono text-2xl font-bold text-emerald-400"
                style={{ fontVariantNumeric: "tabular-nums" }}
              >
                ~{config.recall_estimate.toFixed(1)}%
              </p>
              <p className="text-xs text-[var(--text-primary)]">
                Estimated Recall
              </p>
            </div>
            <div className="h-8 w-px bg-[var(--overlay-strong)]" />
            <div className="text-center">
              <p
                className="font-mono text-2xl font-bold text-brand-500"
                style={{ fontVariantNumeric: "tabular-nums" }}
              >
                {config.candidate_pair_count?.toLocaleString()}
              </p>
              <p className="text-xs text-[var(--text-primary)]">
                Candidate Pairs
              </p>
            </div>
          </GlassCard>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab: Composite Match Rules
// ---------------------------------------------------------------------------

interface CompositeRulesTabProps {
  runId: string;
  fields: string[];
  rules: MdmCompositeRule[];
  onChange: (r: MdmCompositeRule[]) => void;
}

function ConditionRow({
  line,
  idx,
  total,
  fields,
  onChange,
  onRemove,
}: {
  line: MdmRuleLine;
  idx: number;
  total: number;
  fields: string[];
  onChange: (patch: Partial<MdmRuleLine>) => void;
  onRemove: () => void;
}) {
  return (
    <div className="group/cond relative">
      {idx > 0 && (
        <div className="flex items-center gap-3 py-1 pl-4">
          <div className="w-3 border-l-2 border-dashed border-[var(--border-muted)] h-5" />
          <span
            className="text-[11px] font-bold tracking-widest px-2 py-0.5 rounded-full border"
            style={{
              color: "#86bc25",
              borderColor: "rgba(134,188,37,0.3)",
              background: "rgba(134,188,37,0.08)",
            }}
          >
            AND
          </span>
        </div>
      )}
      <div className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-white/[0.025] border border-white/[0.06] hover:border-[var(--border-muted)] transition-all">
        <select
          value={line.field}
          data-testid="mdm-condition-field"
          onChange={(e) => onChange({ field: e.target.value })}
          className={selectCls + " flex-1 min-w-0"}
        >
          {fields.map((f) => (
            <option key={f} value={f}>
              {f}
            </option>
          ))}
        </select>
        <select
          value={line.method}
          data-testid="mdm-condition-method"
          onChange={(e) => onChange({ method: e.target.value as MatchMethod })}
          className={selectCls + " w-44"}
        >
          {MATCH_METHODS.map((m) => (
            <option key={m.value} value={m.value}>
              {m.label}
            </option>
          ))}
        </select>
        <div className="flex items-center gap-2 shrink-0">
          <input
            type="range"
            min={0}
            max={100}
            step={1}
            value={line.threshold}
            onChange={(e) =>
              onChange({ threshold: parseInt(e.target.value, 10) })
            }
            className="h-1.5 w-20 cursor-pointer appearance-none rounded-full accent-[#86BC25]"
            style={{
              background: `linear-gradient(to right, #86BC25 ${line.threshold}%, #374151 ${line.threshold}%)`,
            }}
          />
          <input
            type="number"
            min={0}
            max={100}
            value={line.threshold}
            data-testid="mdm-condition-threshold"
            onChange={(e) =>
              onChange({
                threshold: Math.min(
                  100,
                  Math.max(0, parseInt(e.target.value, 10) || 0),
                ),
              })
            }
            className={`${inputCls} w-14 text-right`}
            style={{ color: "#86BC25" }}
          />
          <span className="text-xs text-[var(--text-primary)] w-3">%</span>
        </div>
        {total > 1 && (
          <button
            onClick={onRemove}
            data-testid="mdm-condition-remove"
            className="shrink-0 opacity-0 group-hover/cond:opacity-100 transition-opacity text-[var(--text-primary)] hover:text-rose-400 p-1 rounded"
          >
            <svg
              className="w-3.5 h-3.5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
            >
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}

function CompositeRuleCard({
  rule,
  idx,
  total,
  fields,
  onChange,
  onRemove,
  onMoveUp,
  onMoveDown,
}: {
  rule: MdmCompositeRule;
  idx: number;
  total: number;
  fields: string[];
  onChange: (patch: Partial<MdmCompositeRule>) => void;
  onRemove: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
}) {
  const [nameEditing, setNameEditing] = useState(false);
  const [nameDraft, setNameDraft] = useState(rule.name);
  const nameInputRef = useRef<HTMLInputElement>(null);

  function commitName() {
    onChange({ name: nameDraft.trim() || rule.name });
    setNameEditing(false);
  }

  function updateCondition(cIdx: number, patch: Partial<MdmRuleLine>) {
    onChange({
      conditions: rule.conditions.map((c, i) =>
        i === cIdx ? { ...c, ...patch } : c,
      ),
    });
  }

  function addCondition() {
    const usedFields = new Set(rule.conditions.map((c) => c.field));
    const nextField = fields.find((f) => !usedFields.has(f)) ?? fields[0];
    onChange({
      conditions: [
        ...rule.conditions,
        { field: nextField, method: "jaro_winkler", threshold: 80 },
      ],
    });
  }

  return (
    <div>
      {idx > 0 && (
        <div className="flex items-center gap-4 my-4">
          <div className="flex-1 border-t border-[var(--border-muted)]" />
          <span className="text-xs font-bold tracking-widest px-3 py-1 rounded-full border border-[var(--border-strong)] text-[var(--text-primary)] bg-white/[0.03]">
            OR
          </span>
          <div className="flex-1 border-t border-[var(--border-muted)]" />
        </div>
      )}

      <div
        className={`rounded-xl border transition-all duration-150 overflow-hidden ${
          rule.enabled
            ? "border-[rgba(134,188,37,0.2)] bg-[rgba(134,188,37,0.03)]"
            : "border-[var(--border-muted)] bg-white/[0.02] opacity-60"
        }`}
      >
        {/* Card header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/[0.06]">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <span
              className="text-[11px] font-bold w-6 h-6 rounded-full flex items-center justify-center shrink-0 border"
              style={{
                color: rule.enabled ? "#86bc25" : "#475569",
                borderColor: rule.enabled
                  ? "rgba(134,188,37,0.35)"
                  : "rgba(255,255,255,0.1)",
                background: rule.enabled
                  ? "rgba(134,188,37,0.12)"
                  : "rgba(255,255,255,0.04)",
              }}
            >
              {rule.priority}
            </span>

            {nameEditing ? (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  commitName();
                }}
                className="flex-1 flex items-center"
              >
                <input
                  ref={nameInputRef}
                  autoFocus
                  value={nameDraft}
                  onChange={(e) => setNameDraft(e.target.value)}
                  onBlur={commitName}
                  onKeyDown={(e) => e.key === "Escape" && setNameEditing(false)}
                  className={`${inputCls} flex-1 text-sm font-medium`}
                />
              </form>
            ) : (
              <button
                className="group/rname flex items-center gap-1.5 text-sm font-medium text-slate-200 hover:text-[var(--text-primary)] transition-colors min-w-0"
                onClick={() => {
                  setNameDraft(rule.name);
                  setNameEditing(true);
                  setTimeout(() => nameInputRef.current?.select(), 0);
                }}
              >
                <span className="truncate">{rule.name}</span>
                <svg
                  className="w-3 h-3 shrink-0 opacity-0 group-hover/rname:opacity-100 text-[var(--text-primary)] transition-opacity"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                >
                  <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
                  <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
              </button>
            )}
          </div>

          <div className="flex items-center gap-1.5 shrink-0">
            <button
              onClick={onMoveUp}
              disabled={idx === 0}
              className="p-1 text-[var(--text-primary)] hover:text-slate-300 disabled:opacity-25 transition-colors"
            >
              <svg
                className="w-3.5 h-3.5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
              >
                <path d="M18 15l-6-6-6 6" />
              </svg>
            </button>
            <button
              onClick={onMoveDown}
              disabled={idx === total - 1}
              className="p-1 text-[var(--text-primary)] hover:text-slate-300 disabled:opacity-25 transition-colors"
            >
              <svg
                className="w-3.5 h-3.5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
              >
                <path d="M6 9l6 6 6-6" />
              </svg>
            </button>
            <button
              onClick={() => onChange({ enabled: !rule.enabled })}
              data-testid="mdm-rule-toggle-enabled"
              className="flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-lg border transition-all"
              style={
                rule.enabled
                  ? {
                      color: "#86bc25",
                      borderColor: "rgba(134,188,37,0.3)",
                      background: "rgba(134,188,37,0.08)",
                    }
                  : {
                      color: "#64748b",
                      borderColor: "rgba(255,255,255,0.08)",
                      background: "rgba(255,255,255,0.03)",
                    }
              }
            >
              <span
                className={`w-1.5 h-1.5 rounded-full ${rule.enabled ? "bg-[#86bc25]" : "bg-slate-600"}`}
              />
              {rule.enabled ? "Active" : "Disabled"}
            </button>
            <button
              onClick={onRemove}
              className="p-1 text-[var(--text-primary)] hover:text-rose-400 transition-colors rounded"
            >
              <svg
                className="w-3.5 h-3.5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" />
                <path d="M10 11v6M14 11v6M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2" />
              </svg>
            </button>
          </div>
        </div>

        {/* Conditions */}
        <div className="px-3 py-3 space-y-0">
          <div className="flex items-center gap-2 px-4 pb-2">
            <span className="flex-1 text-[11px] font-semibold uppercase tracking-widest text-[var(--text-primary)]">
              Field
            </span>
            <span className="w-44 text-[11px] font-semibold uppercase tracking-widest text-[var(--text-primary)]">
              Method
            </span>
            <span className="w-36 text-[11px] font-semibold uppercase tracking-widest text-[var(--text-primary)]">
              Threshold
            </span>
            <span className="w-5" />
          </div>
          {rule.conditions.map((cond, cIdx) => (
            <ConditionRow
              key={cIdx}
              line={cond}
              idx={cIdx}
              total={rule.conditions.length}
              fields={fields}
              onChange={(patch) => updateCondition(cIdx, patch)}
              onRemove={() =>
                onChange({
                  conditions: rule.conditions.filter((_, i) => i !== cIdx),
                })
              }
            />
          ))}
          <div className={rule.conditions.length > 0 ? "pt-1 pl-4" : ""}>
            {rule.conditions.length > 0 && (
              <div className="flex items-center gap-3 py-1">
                <div className="w-3 border-l-2 border-dashed border-[var(--border-muted)] h-5" />
              </div>
            )}
            <button
              onClick={addCondition}
              data-testid="mdm-condition-add"
              disabled={rule.conditions.length >= fields.length}
              className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-dashed border-[var(--border-muted)] text-[var(--text-primary)] hover:text-slate-300 hover:border-[var(--border-strong)] transition-all disabled:opacity-30"
            >
              <svg
                className="w-3.5 h-3.5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
              >
                <path d="M12 5v14M5 12h14" />
              </svg>
              Add condition
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function CompositeRulesTab({
  runId,
  fields,
  rules,
  onChange,
}: CompositeRulesTabProps) {
  const [loading, setLoading] = useState(false);
  const [suggestion, setSuggestion] = useState<MdmMatchConfigSuggestion | null>(
    null,
  );
  const [panelOpen, setPanelOpen] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  function updateRule(idx: number, patch: Partial<MdmCompositeRule>) {
    onChange(rules.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  }

  function removeRule(idx: number) {
    onChange(
      rules
        .filter((_, i) => i !== idx)
        .map((r, i) => ({ ...r, priority: i + 1 })),
    );
  }

  function addRule() {
    const usedFields = new Set(
      rules.flatMap((r) => r.conditions.map((c) => c.field)),
    );
    const nextField = fields.find((f) => !usedFields.has(f)) ?? fields[0];
    onChange([
      ...rules,
      {
        id: uid(),
        name: `Rule ${rules.length + 1}`,
        enabled: true,
        priority: rules.length + 1,
        conditions: [
          { field: nextField, method: "jaro_winkler", threshold: 80 },
        ],
      },
    ]);
  }

  function moveRule(idx: number, dir: -1 | 1) {
    const next = [...rules];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    onChange(next.map((r, i) => ({ ...r, priority: i + 1 })));
  }

  async function requestSuggestion() {
    if (loading) return;
    setPanelOpen(true);
    if (suggestion) return;
    setLoading(true);
    try {
      const s = await suggestMatchConfig(runId);
      setSuggestion(s);
    } catch {
      setPanelOpen(false);
    } finally {
      setLoading(false);
    }
  }

  const enabledCount = rules.filter((r) => r.enabled).length;

  return (
    <div className="space-y-4 p-6">
      {!dismissed && (
        <div
          className="rounded-xl border px-4 py-3"
          style={{
            background: "rgba(134,188,37,0.06)",
            borderColor: "rgba(134,188,37,0.25)",
          }}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <svg
                className="h-4 w-4 shrink-0"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#86BC25"
                strokeWidth="2"
              >
                <path d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 17l-6.2 4.3 2.4-7.4L2 9.4h7.6z" />
              </svg>
              <div>
                <p
                  className="text-xs font-semibold"
                  style={{ color: "#86BC25" }}
                >
                  AI Rule Suggestions
                  <span
                    className="ml-2 rounded-full px-1.5 py-0.5 text-[11px] font-medium"
                    style={{
                      background: "rgba(134,188,37,0.15)",
                      color: "#86BC25",
                    }}
                  >
                    Advisory
                  </span>
                </p>
                <p className="mt-0.5 text-xs text-[var(--text-primary)]">
                  {loading
                    ? "Analysing your fields…"
                    : suggestion
                      ? "Suggestions loaded — review diff below"
                      : "AI can recommend matching rule structures based on field semantics."}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {!panelOpen && (
                <button
                  onClick={requestSuggestion}
                  disabled={loading}
                  className="rounded-lg px-3 py-1.5 text-xs font-semibold transition-all disabled:opacity-40"
                  style={{
                    background: "rgba(134,188,37,0.15)",
                    color: "#86BC25",
                    border: "1px solid rgba(134,188,37,0.3)",
                  }}
                >
                  {loading ? "Loading…" : "Load Suggestions"}
                </button>
              )}
              <button
                onClick={() => {
                  setSuggestion(null);
                  setPanelOpen(false);
                  setDismissed(true);
                }}
                className="rounded p-1 text-[var(--text-primary)] hover:text-[var(--text-primary)]"
              >
                <svg
                  className="h-3.5 w-3.5"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                >
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <p className="text-sm font-semibold text-slate-200">Matching Rules</p>
          <span className="text-[11px] px-2 py-0.5 rounded-full bg-[var(--overlay-subtle)] text-[var(--text-primary)] border border-[var(--border-muted)]">
            {enabledCount} of {rules.length} active
          </span>
        </div>
        <span className="text-[11px] text-[var(--text-primary)] hidden sm:block">
          A pair matches if&nbsp;
          <span className="text-[var(--text-primary)] font-medium">any</span>
          &nbsp;rule fires ·&nbsp;
          <span className="text-[var(--text-primary)] font-medium">all</span>
          &nbsp;conditions within a rule must pass
        </span>
      </div>

      <div>
        {rules.map((rule, idx) => (
          <CompositeRuleCard
            key={rule.id}
            rule={rule}
            idx={idx}
            total={rules.length}
            fields={fields}
            onChange={(patch) => updateRule(idx, patch)}
            onRemove={() => removeRule(idx)}
            onMoveUp={() => moveRule(idx, -1)}
            onMoveDown={() => moveRule(idx, 1)}
          />
        ))}
      </div>

      <button
        onClick={addRule}
        data-testid="mdm-rule-add"
        className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border-2 border-dashed border-[var(--border-muted)] text-sm text-[var(--text-primary)] hover:text-slate-300 hover:border-[var(--border-strong)] transition-all"
      >
        <svg
          className="w-4 h-4"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
        >
          <path d="M12 5v14M5 12h14" />
        </svg>
        Add Rule
      </button>

      <div className="flex items-center gap-5 pt-1">
        <div className="flex items-center gap-1.5">
          <span
            className="text-[11px] font-bold px-2 py-0.5 rounded-full border"
            style={{
              color: "#86bc25",
              borderColor: "rgba(134,188,37,0.3)",
              background: "rgba(134,188,37,0.08)",
            }}
          >
            AND
          </span>
          <span className="text-[11px] text-[var(--text-primary)]">
            All conditions in rule must pass
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-[11px] font-bold px-2 py-0.5 rounded-full border border-[var(--border-strong)] text-[var(--text-primary)] bg-white/[0.03]">
            OR
          </span>
          <span className="text-[11px] text-[var(--text-primary)]">
            Any rule can trigger a match
          </span>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab: Match Rules
// ---------------------------------------------------------------------------

interface MatchRulesTabProps {
  runId: string;
  rules: MdmMatchRule[];
  onChange: (r: MdmMatchRule[]) => void;
}

interface SuggestionDiff {
  field: string;
  oldMethod: MatchMethod;
  newMethod: MatchMethod;
  oldWeight: number;
  newWeight: number;
  oldM: number;
  newM: number;
  oldU: number;
  newU: number;
  accepted: boolean;
}

function MatchRulesTab({ runId, rules, onChange }: MatchRulesTabProps) {
  const [loading, setLoading] = useState(false);
  const [suggestion, setSuggestion] = useState<MdmMatchConfigSuggestion | null>(
    null,
  );
  const [diffs, setDiffs] = useState<SuggestionDiff[] | null>(null);
  // Advisory panel: collapsed by default, never auto-fires
  const [panelOpen, setPanelOpen] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  function updateRule(idx: number, patch: Partial<MdmMatchRule>) {
    const next = rules.map((r, i) => (i === idx ? { ...r, ...patch } : r));
    onChange(next);
  }

  async function requestSuggestion() {
    if (loading) return;
    setPanelOpen(true);
    if (suggestion) return; // already loaded, just re-open
    setLoading(true);
    try {
      const s = await suggestMatchConfig(runId);
      setSuggestion(s);
      const d: SuggestionDiff[] = s.suggested_rules.map((sr) => {
        const cur = rules.find((r) => r.field === sr.field);
        return {
          field: sr.field,
          oldMethod: cur?.method ?? "exact",
          newMethod: sr.method,
          oldWeight: cur?.weight ?? 1.0,
          newWeight: sr.weight,
          oldM: cur?.m_probability ?? 0.9,
          newM: sr.m_probability,
          oldU: cur?.u_probability ?? 0.1,
          newU: sr.u_probability,
          accepted: false,
        };
      });
      setDiffs(d);
    } catch {
      // LLM unavailable — close panel gracefully, don't block the user
      setPanelOpen(false);
    } finally {
      setLoading(false);
    }
  }

  function acceptAll() {
    if (!suggestion) return;
    const next = rules.map((r) => {
      const sr = suggestion.suggested_rules.find((s) => s.field === r.field);
      return sr ? { ...r, ...sr } : r;
    });
    onChange(next);
    setDiffs(null);
    setSuggestion(null);
    setPanelOpen(false);
    setDismissed(true);
  }

  function dismiss() {
    setDiffs(null);
    setSuggestion(null);
    setPanelOpen(false);
    setDismissed(true);
  }

  return (
    <div className="space-y-4 p-6">
      {/* Advisory AI chip — always visible, collapsed by default */}
      {!dismissed && (
        <div
          className="rounded-xl border px-4 py-3"
          style={{
            background: "rgba(134,188,37,0.06)",
            borderColor: "rgba(134,188,37,0.25)",
          }}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              {/* Sparkle icon */}
              <svg
                className="h-4 w-4 shrink-0"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#86BC25"
                strokeWidth="2"
              >
                <path d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 17l-6.2 4.3 2.4-7.4L2 9.4h7.6z" />
              </svg>
              <div>
                <p
                  className="text-xs font-semibold"
                  style={{ color: "#86BC25" }}
                >
                  AI Match Rule Suggestions
                  <span
                    className="ml-2 rounded-full px-1.5 py-0.5 text-[11px] font-medium"
                    style={{
                      background: "rgba(134,188,37,0.15)",
                      color: "#86BC25",
                    }}
                  >
                    Advisory
                  </span>
                </p>
                <p className="mt-0.5 text-xs text-[var(--text-primary)]">
                  {panelOpen && loading
                    ? "Analyzing your data fields…"
                    : suggestion
                      ? "Suggestions ready — review below"
                      : "AI can recommend match methods and weights based on your field types. Optional."}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {!panelOpen && (
                <button
                  onClick={requestSuggestion}
                  disabled={loading}
                  className="rounded-lg px-3 py-1.5 text-xs font-semibold transition-all disabled:opacity-40"
                  style={{
                    background: "rgba(134,188,37,0.15)",
                    color: "#86BC25",
                    border: "1px solid rgba(134,188,37,0.3)",
                  }}
                >
                  {loading ? "Loading…" : "Load Suggestions"}
                </button>
              )}
              {panelOpen && (
                <button
                  onClick={() => setPanelOpen(false)}
                  className="rounded-lg px-3 py-1.5 text-xs font-semibold text-[var(--text-primary)] transition-all hover:text-slate-200"
                  style={{
                    background: "rgba(255,255,255,0.05)",
                    border: "1px solid rgba(255,255,255,0.08)",
                  }}
                >
                  Collapse
                </button>
              )}
              <button
                onClick={dismiss}
                className="rounded p-1 text-[var(--text-primary)] transition-colors hover:text-[var(--text-primary)]"
                title="Dismiss suggestions"
              >
                <svg
                  className="h-3.5 w-3.5"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                >
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Loading spinner inside panel */}
          {panelOpen && loading && (
            <div className="mt-3 flex items-center gap-2 border-t border-[var(--border-muted)] pt-3">
              <svg
                className="h-4 w-4 animate-spin text-[var(--text-primary)]"
                viewBox="0 0 24 24"
                fill="none"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v8H4z"
                />
              </svg>
              <span className="text-xs text-[var(--text-primary)]">
                Analyzing field types and recommending match configuration…
              </span>
            </div>
          )}

          {/* Diff panel — only when open, loaded, and not loading */}
          {panelOpen && !loading && diffs && suggestion && (
            <div className="mt-3 border-t border-[var(--border-muted)] pt-3">
              <div className="mb-3 flex items-center justify-between">
                <p className="text-xs text-[var(--text-primary)]">
                  {suggestion.rationale}
                </p>
                <div className="flex gap-2">
                  <Btn onClick={acceptAll} variant="success" size="sm">
                    Accept All
                  </Btn>
                  <Btn onClick={dismiss} variant="ghost" size="sm">
                    Dismiss
                  </Btn>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table
                  className="w-full text-xs"
                  style={{ fontVariantNumeric: "tabular-nums" }}
                >
                  <thead>
                    <tr className="border-b border-[var(--border-muted)] text-[var(--text-primary)]">
                      <th className="pb-2 pr-4 text-left font-medium">Field</th>
                      <th className="pb-2 pr-4 text-left font-medium">
                        Method
                      </th>
                      <th className="pb-2 pr-4 text-right font-medium">
                        Weight
                      </th>
                      <th className="pb-2 pr-4 text-right font-medium">
                        m-prob
                      </th>
                      <th className="pb-2 text-right font-medium">u-prob</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {diffs.map((d) => (
                      <tr key={d.field}>
                        <td className="py-1.5 pr-4 font-mono text-slate-300">
                          {d.field}
                        </td>
                        <td className="py-1.5 pr-4">
                          {d.oldMethod !== d.newMethod ? (
                            <span className="space-x-2">
                              <span className="text-[var(--text-primary)] line-through">
                                {d.oldMethod}
                              </span>
                              <span className="text-emerald-400">
                                {d.newMethod}
                              </span>
                            </span>
                          ) : (
                            <span className="text-[var(--text-primary)]">
                              {d.newMethod}
                            </span>
                          )}
                        </td>
                        <td className="py-1.5 pr-4 text-right">
                          {d.oldWeight !== d.newWeight ? (
                            <span className="space-x-1">
                              <span className="text-[var(--text-primary)] line-through">
                                {d.oldWeight.toFixed(1)}
                              </span>
                              <span className="text-emerald-400">
                                {d.newWeight.toFixed(1)}
                              </span>
                            </span>
                          ) : (
                            <span className="text-[var(--text-primary)]">
                              {d.newWeight.toFixed(1)}
                            </span>
                          )}
                        </td>
                        <td className="py-1.5 pr-4 text-right">
                          {d.oldM !== d.newM ? (
                            <span className="space-x-1">
                              <span className="text-[var(--text-primary)] line-through">
                                {d.oldM.toFixed(2)}
                              </span>
                              <span className="text-emerald-400">
                                {d.newM.toFixed(2)}
                              </span>
                            </span>
                          ) : (
                            <span className="text-[var(--text-primary)]">
                              {d.newM.toFixed(2)}
                            </span>
                          )}
                        </td>
                        <td className="py-1.5 text-right">
                          {d.oldU !== d.newU ? (
                            <span className="space-x-1">
                              <span className="text-[var(--text-primary)] line-through">
                                {d.oldU.toFixed(2)}
                              </span>
                              <span className="text-emerald-400">
                                {d.newU.toFixed(2)}
                              </span>
                            </span>
                          ) : (
                            <span className="text-[var(--text-primary)]">
                              {d.newU.toFixed(2)}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Rules table */}
      <GlassCard>
        <div className="overflow-x-auto">
          <table
            className="w-full"
            style={{ fontVariantNumeric: "tabular-nums" }}
          >
            <thead>
              <tr className="border-b border-[var(--border-muted)]">
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[var(--text-primary)]">
                  Field
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[var(--text-primary)]">
                  Method
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[var(--text-primary)]">
                  Threshold
                  <span className="ml-1 font-normal normal-case text-[var(--text-primary)]">
                    0–100%
                  </span>
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[var(--text-primary)]">
                  Weight
                  <span className="ml-1 font-normal normal-case text-[var(--text-primary)]">
                    0.1–3.0
                  </span>
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[var(--text-primary)]">
                  m-prob
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[var(--text-primary)]">
                  u-prob
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {rules.map((rule, idx) => {
                const threshold = rule.threshold ?? 80;
                return (
                  <tr
                    key={rule.field}
                    className="group transition-colors hover:bg-[var(--overlay-faint)]"
                  >
                    <td className="px-4 py-3">
                      <span className="font-mono text-xs text-slate-300">
                        {rule.field}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <select
                        value={rule.method}
                        onChange={(e) =>
                          updateRule(idx, {
                            method: e.target.value as MatchMethod,
                          })
                        }
                        className={selectCls}
                      >
                        {MATCH_METHODS.map((m) => (
                          <option key={m.value} value={m.value}>
                            {m.label}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <input
                          type="range"
                          min={0}
                          max={100}
                          step={1}
                          value={threshold}
                          onChange={(e) =>
                            updateRule(idx, {
                              threshold: parseInt(e.target.value, 10),
                            })
                          }
                          className="h-1.5 w-24 cursor-pointer appearance-none rounded-full accent-[#86BC25]"
                          style={{
                            background: `linear-gradient(to right, #86BC25 ${threshold}%, #374151 ${threshold}%)`,
                          }}
                        />
                        <input
                          type="number"
                          min={0}
                          max={100}
                          step={1}
                          value={threshold}
                          onChange={(e) => {
                            const v = Math.min(
                              100,
                              Math.max(0, parseInt(e.target.value, 10) || 0),
                            );
                            updateRule(idx, { threshold: v });
                          }}
                          className={`${inputCls} w-14 text-right`}
                          style={{ color: "#86BC25" }}
                        />
                        <span className="text-xs text-[var(--text-primary)]">
                          %
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <input
                          type="range"
                          min={0.1}
                          max={3.0}
                          step={0.1}
                          value={rule.weight}
                          onChange={(e) =>
                            updateRule(idx, {
                              weight: parseFloat(e.target.value),
                            })
                          }
                          className="h-1.5 w-28 cursor-pointer appearance-none rounded-full bg-slate-700 accent-brand-500"
                        />
                        <span className="w-8 text-right text-xs font-mono text-brand-500">
                          {rule.weight.toFixed(1)}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <input
                        type="number"
                        min={0}
                        max={1}
                        step={0.01}
                        value={rule.m_probability}
                        onChange={(e) =>
                          updateRule(idx, {
                            m_probability: parseFloat(e.target.value),
                          })
                        }
                        className={`${inputCls} w-20 text-right`}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <input
                        type="number"
                        min={0}
                        max={1}
                        step={0.01}
                        value={rule.u_probability}
                        onChange={(e) =>
                          updateRule(idx, {
                            u_probability: parseFloat(e.target.value),
                          })
                        }
                        className={`${inputCls} w-20 text-right`}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </GlassCard>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab: Confidence Bands
// ---------------------------------------------------------------------------

interface ConfidenceTabProps {
  bands: MdmConfidenceBands;
  onChange: (b: MdmConfidenceBands) => void;
  skipLlmAdjudication: boolean;
  onSkipLlmChange: (v: boolean) => void;
}

function ConfidenceTab({
  bands,
  onChange,
  skipLlmAdjudication,
  onSkipLlmChange,
}: ConfidenceTabProps) {
  const am = bands.auto_merge_threshold;
  const hl = bands.hitl_lower_bound;

  function setMerge(v: number) {
    const safe = Math.max(v, hl + 0.02);
    onChange({ ...bands, auto_merge_threshold: Math.min(safe, 1.0) });
  }

  function setHitl(v: number) {
    const safe = Math.min(v, am - 0.02);
    onChange({ ...bands, hitl_lower_bound: Math.max(safe, 0.0) });
  }

  // Band widths as percentages
  const mergeWidth = (1.0 - am) * 100;
  const hitlWidth = (am - hl) * 100;
  const rejectWidth = hl * 100;

  return (
    <div className="space-y-6 p-6">
      {/* Visual band bar */}
      <GlassCard className="p-5">
        <p className="mb-4 text-xs font-semibold uppercase tracking-widest text-[var(--text-primary)]">
          Confidence Band Zones
        </p>
        {/* Score axis runs 0.00 (left) → 1.00 (right): Reject on the low end,
            Auto-Merge on the high end. Segment ORDER must match that axis. */}
        <div className="flex h-10 w-full overflow-hidden rounded-lg">
          <div
            className="flex items-center justify-center text-xs font-semibold text-rose-100 transition-all"
            style={{
              width: `${rejectWidth}%`,
              background: "rgba(239,68,68,0.55)",
            }}
          >
            {rejectWidth > 8 ? "Reject" : ""}
          </div>
          <div
            className="flex items-center justify-center text-xs font-semibold text-amber-900 transition-all"
            style={{
              width: `${hitlWidth}%`,
              background: "rgba(245,158,11,0.75)",
            }}
          >
            {hitlWidth > 16 ? "Manual Review" : hitlWidth > 6 ? "Review" : ""}
          </div>
          <div
            className="flex items-center justify-center text-xs font-semibold text-emerald-900 transition-all"
            style={{
              width: `${mergeWidth}%`,
              background: "rgba(16,185,129,0.75)",
            }}
          >
            {mergeWidth > 10 ? "Auto-Merge" : ""}
          </div>
        </div>
        {/* Tick labels aligned to the actual band boundaries (hl, am), not
            evenly spaced — otherwise the numbers don't sit under their edges. */}
        <div
          className="relative mt-2 h-4 text-xs text-[var(--text-primary)]"
          style={{ fontVariantNumeric: "tabular-nums" }}
        >
          <span className="absolute left-0">0.00</span>
          <span
            className="absolute -translate-x-1/2 text-amber-500"
            style={{ left: `${hl * 100}%` }}
          >
            {hl.toFixed(2)}
          </span>
          <span
            className="absolute -translate-x-1/2 text-emerald-500"
            style={{ left: `${am * 100}%` }}
          >
            {am.toFixed(2)}
          </span>
          <span className="absolute right-0">1.00</span>
        </div>
      </GlassCard>

      {/* Sliders */}
      <GlassCard className="divide-y divide-white/8">
        <div className="flex items-center gap-6 px-5 py-4">
          <div className="w-48 shrink-0">
            <p className="text-sm font-medium text-slate-200">
              Auto-Merge Threshold
            </p>
            <p className="text-xs text-[var(--text-primary)]">
              Pairs above this score merge automatically
            </p>
          </div>
          <div className="flex flex-1 items-center gap-4">
            <input
              type="range"
              min={0.0}
              max={1.0}
              step={0.01}
              value={am}
              data-testid="mdm-confidence-auto-merge"
              onChange={(e) => setMerge(parseFloat(e.target.value))}
              className="h-1.5 w-full cursor-pointer appearance-none rounded-full accent-emerald-500"
              style={{
                background: `linear-gradient(to right, #10b981 ${am * 100}%, #374151 ${am * 100}%)`,
              }}
            />
            <span
              className="w-12 text-right font-mono text-sm font-bold text-emerald-400"
              style={{ fontVariantNumeric: "tabular-nums" }}
            >
              {am.toFixed(2)}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-6 px-5 py-4">
          <div className="w-48 shrink-0">
            <p className="text-sm font-medium text-slate-200">
              Manual Review Lower Bound
            </p>
            <p className="text-xs text-[var(--text-primary)]">
              Pairs below this score are rejected
            </p>
          </div>
          <div className="flex flex-1 items-center gap-4">
            <input
              type="range"
              min={0.0}
              max={1.0}
              step={0.01}
              value={hl}
              data-testid="mdm-confidence-hitl-lower"
              onChange={(e) => setHitl(parseFloat(e.target.value))}
              className="h-1.5 w-full cursor-pointer appearance-none rounded-full accent-amber-500"
              style={{
                background: `linear-gradient(to right, #ef4444 ${hl * 100}%, #f59e0b ${hl * 100}%, #f59e0b ${am * 100}%, #374151 ${am * 100}%)`,
              }}
            />
            <span
              className="w-12 text-right font-mono text-sm font-bold text-amber-400"
              style={{ fontVariantNumeric: "tabular-nums" }}
            >
              {hl.toFixed(2)}
            </span>
          </div>
        </div>
      </GlassCard>

      {/* Timeout config */}
      <GlassCard className="p-5">
        <p className="mb-4 text-xs font-semibold uppercase tracking-widest text-[var(--text-primary)]">
          Manual Review Timeout
        </p>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <input
              type="number"
              min={1}
              max={720}
              value={bands.timeout_hours}
              onChange={(e) =>
                onChange({
                  ...bands,
                  timeout_hours: parseInt(e.target.value, 10),
                })
              }
              className={`${inputCls} w-20 text-center`}
            />
            <span className="text-sm text-[var(--text-primary)]">
              hours, then
            </span>
          </div>
          <select
            value={bands.timeout_behavior}
            onChange={(e) =>
              onChange({
                ...bands,
                timeout_behavior: e.target
                  .value as MdmConfidenceBands["timeout_behavior"],
              })
            }
            className={selectCls}
          >
            <option value="auto_reject">Auto-reject</option>
            <option value="auto_merge">Auto-merge</option>
            <option value="escalate">Escalate to manager</option>
          </select>
        </div>
        <p className="mt-2 text-xs text-[var(--text-primary)]">
          Pairs in the Manual Review queue that exceed this window will be
          handled by the selected fallback.
        </p>
      </GlassCard>

      <GlassCard className="p-5">
        <label className="flex cursor-pointer items-start gap-3">
          <input
            type="checkbox"
            data-testid="mdm-skip-llm-adjudication"
            checked={skipLlmAdjudication}
            onChange={(e) => onSkipLlmChange(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-[var(--border-strong)] bg-transparent accent-brand-500"
          />
          <div>
            <p className="text-sm font-medium text-slate-200">
              Skip LLM adjudication
            </p>
            <p className="mt-1 text-xs text-[var(--text-primary)]">
              Review-zone pairs go straight to the Manual Review queue instead
              of one LLM call per pair. Use this when the review zone is
              expected to be large (e.g. a weak blocking/matching key) and you'd
              rather bulk-decide or review the queue directly than pay for LLM
              adjudication on every pair.
            </p>
          </div>
        </label>
      </GlassCard>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab: Survivorship
// ---------------------------------------------------------------------------

interface SurvivorshipTabProps {
  rules: MdmSurvivorshipRule[];
  onChange: (r: MdmSurvivorshipRule[]) => void;
  // canonical field name → registered source origin (e.g.
  // "oracle:TXCWDEMO/PROVIDER"). Lets reviewers see which DB/table each
  // prioritized column comes from.
  fieldSources?: Record<string, string>;
}

// Split a registered source name into a DB and table portion for display.
// Names look like "oracle:TXCWDEMO/PROVIDER" (provider:db/table) or
// "upload:people.csv"; fall back to showing the whole string when it doesn't
// parse.
function splitSource(src: string | undefined): { db: string; table: string } {
  if (!src) return { db: "—", table: "—" };
  const afterProvider = src.includes(":")
    ? src.slice(src.indexOf(":") + 1)
    : src;
  if (afterProvider.includes("/")) {
    const [db, ...rest] = afterProvider.split("/");
    return { db: db || "—", table: rest.join("/") || "—" };
  }
  return { db: afterProvider || "—", table: "—" };
}

function SurvivorshipTab({
  rules,
  onChange,
  fieldSources = {},
}: SurvivorshipTabProps) {
  const [dragging, setDragging] = useState<number | null>(null);
  const [dragOver, setDragOver] = useState<number | null>(null);

  function updateRule(idx: number, patch: Partial<MdmSurvivorshipRule>) {
    onChange(rules.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  }

  function handleDragStart(idx: number) {
    setDragging(idx);
  }

  function handleDragEnter(idx: number) {
    setDragOver(idx);
  }

  function handleDrop() {
    if (dragging === null || dragOver === null || dragging === dragOver) {
      setDragging(null);
      setDragOver(null);
      return;
    }
    const next = [...rules];
    const [item] = next.splice(dragging, 1);
    next.splice(dragOver, 0, item);
    onChange(next);
    setDragging(null);
    setDragOver(null);
  }

  return (
    <div className="p-6">
      <p className="mb-4 text-xs text-[var(--text-primary)]">
        Drag rows to reorder Source Priority. Changes apply when strategy is set
        to <span className="font-mono text-slate-300">source_priority</span>.
      </p>
      <GlassCard>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[var(--border-muted)]">
                <th className="w-8 px-4 py-3" />
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[var(--text-primary)]">
                  Source DB
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[var(--text-primary)]">
                  Source Table
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[var(--text-primary)]">
                  Field
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[var(--text-primary)]">
                  Survivorship Strategy
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[var(--text-primary)]">
                  Notes
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {rules.map((rule, idx) => {
                const isLlm = rule.strategy === "llm_assisted";
                const isDraggingOver =
                  dragOver === idx && dragging !== null && dragging !== idx;
                return (
                  <tr
                    key={rule.field}
                    draggable
                    onDragStart={() => handleDragStart(idx)}
                    onDragEnter={() => handleDragEnter(idx)}
                    onDragEnd={handleDrop}
                    onDragOver={(e) => e.preventDefault()}
                    className={`group cursor-grab transition-colors ${
                      isDraggingOver
                        ? "bg-brand-500/10 outline outline-1 outline-brand-500/40"
                        : "hover:bg-[var(--overlay-faint)]"
                    } ${dragging === idx ? "opacity-40" : ""}`}
                  >
                    <td className="px-4 py-3 text-[var(--text-primary)] group-hover:text-[var(--text-primary)]">
                      <svg
                        className="h-4 w-4"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                      >
                        <path
                          d="M4 8h16M4 12h16M4 16h16"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          fill="none"
                        />
                      </svg>
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-mono text-xs text-[var(--text-primary)]">
                        {splitSource(fieldSources[rule.field]).db}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-mono text-xs text-[var(--text-primary)]">
                        {splitSource(fieldSources[rule.field]).table}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-mono text-xs text-slate-300">
                        {rule.field}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <select
                        value={rule.strategy}
                        data-testid="mdm-survivorship-strategy"
                        data-field={rule.field}
                        onChange={(e) =>
                          updateRule(idx, {
                            strategy: e.target.value as SurvivorshipStrategy,
                          })
                        }
                        className={selectCls}
                      >
                        {SURVIVORSHIP_STRATEGIES.map((s) => (
                          <option key={s.value} value={s.value}>
                            {s.label}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-3">
                      {isLlm && (
                        <Badge variant="amber">
                          LLM-Assisted — review output before production
                        </Badge>
                      )}
                      {rule.strategy === "source_priority" && (
                        <span className="text-xs text-[var(--text-primary)]">
                          Drag to set order
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </GlassCard>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab: Force-HITL Overrides
// ---------------------------------------------------------------------------

interface ForceHitlTabProps {
  rules: MdmForceHitlRule[];
  fields: string[];
  onChange: (r: MdmForceHitlRule[]) => void;
}

function ForceHitlTab({ rules, fields, onChange }: ForceHitlTabProps) {
  function addRule() {
    onChange([
      ...rules,
      { id: uid(), field: fields[0] ?? "", operator: "eq", value: "" },
    ]);
  }

  function removeRule(id: string) {
    onChange(rules.filter((r) => r.id !== id));
  }

  function updateRule(id: string, patch: Partial<MdmForceHitlRule>) {
    onChange(rules.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  }

  return (
    <div className="space-y-5 p-6">
      {/* Intent description */}
      <GlassCard className="flex items-start gap-3 p-4">
        <svg
          className="mt-0.5 h-4 w-4 shrink-0 text-amber-400"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
        </svg>
        <div>
          <p className="text-sm font-medium text-slate-200">
            Override Behavior
          </p>
          <p className="mt-0.5 text-xs text-[var(--text-primary)]">
            These rules force a candidate pair to steward review{" "}
            <span className="text-slate-300">
              even when the confidence score is 0.99
            </span>{" "}
            or above. Use them to protect high-risk fields — sensitive PII,
            regulated identifiers, or fields where an automated merge would
            require remediation work to undo.
          </p>
        </div>
      </GlassCard>

      {/* Rules list */}
      <div className="space-y-2">
        {rules.map((rule) => (
          <GlassCard key={rule.id} className="flex items-center gap-3 p-3">
            <div className="flex flex-1 flex-wrap items-center gap-2">
              <span className="text-xs text-[var(--text-primary)]">If</span>
              <select
                value={rule.field}
                onChange={(e) => updateRule(rule.id, { field: e.target.value })}
                className={selectCls}
              >
                {fields.map((f) => (
                  <option key={f} value={f}>
                    {f}
                  </option>
                ))}
              </select>
              <select
                value={rule.operator}
                onChange={(e) =>
                  updateRule(rule.id, {
                    operator: e.target.value as OverrideOperator,
                  })
                }
                className={selectCls}
              >
                {OVERRIDE_OPERATORS.map((op) => (
                  <option key={op.value} value={op.value}>
                    {op.label}
                  </option>
                ))}
              </select>
              {rule.operator !== "is_true" && (
                <input
                  type="text"
                  value={rule.value}
                  onChange={(e) =>
                    updateRule(rule.id, { value: e.target.value })
                  }
                  placeholder={
                    rule.operator === "in" ? "val1, val2, val3" : "value"
                  }
                  className={`${inputCls} min-w-28`}
                />
              )}
              <span className="text-xs text-[var(--text-primary)]">
                → force Manual Review
              </span>
              <Badge variant="amber">Manual Review Override</Badge>
            </div>
            <button
              onClick={() => removeRule(rule.id)}
              className="ml-auto shrink-0 rounded-lg p-1 text-[var(--text-primary)] transition-colors hover:bg-rose-500/10 hover:text-rose-400 focus:outline-none"
              aria-label="Remove rule"
            >
              <svg
                className="h-4 w-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </GlassCard>
        ))}

        {rules.length === 0 && (
          <p className="py-6 text-center text-sm text-[var(--text-primary)]">
            No force Manual Review rules defined. Confidence bands govern all
            routing.
          </p>
        )}
      </div>

      <Btn onClick={addRule} variant="ghost" size="sm">
        <svg
          className="h-3.5 w-3.5"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M12 5v14M5 12h14" />
        </svg>
        Add Rule
      </Btn>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function MdmRuleBuilderView({
  runId,
  projectId,
  canonicalFields,
  entityType: entityTypeProp,
  onTestRun,
}: MdmRuleBuilderViewProps) {
  const { runStatus, setRunStatus, setRun, setEntityType } = useMdmRunStore();
  const [actualRunStatus, setActualRunStatus] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabId>("blocking");
  const [testLoading, setTestLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [debugError, setDebugError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [configName, setConfigName] = useState("Config v1");
  const [versionHistory, setVersionHistory] = useState<VersionEntry[]>([]);
  const [showVersionPanel, setShowVersionPanel] = useState(false);

  // Per-tab state
  const [blocking, setBlocking] = useState<MdmBlockingConfig>(() => {
    const initialKeys = canonicalFields.slice(0, 2);
    return {
      strategy: recommendBlockingStrategy(initialKeys),
      blocking_keys: initialKeys,
    };
  });

  // Initialized empty; the getFields effect seeds these from the discovered
  // source fields (defaultCompositeRules on an empty list would otherwise emit
  // placeholder rules referencing a nonexistent "name" field).
  const [matchRules, setMatchRules] = useState<MdmMatchRule[]>(
    defaultMatchRules(canonicalFields),
  );
  const [compositeRules, setCompositeRules] = useState<MdmCompositeRule[]>(
    canonicalFields.length ? defaultCompositeRules(canonicalFields) : [],
  );

  const [confidenceBands, setConfidenceBands] = useState<MdmConfidenceBands>({
    // Auto-merge >=0.80, manual review 0.50-0.80, reject <0.50. The old
    // 0.98/0.92 defaults never auto-merged real data (name-match scores top
    // out ~0.84), so every pair went to manual review. Maps to the backend
    // auto_match_threshold / no_match_threshold (see mdm-client saveConfig).
    auto_merge_threshold: 0.8,
    hitl_lower_bound: 0.5,
    timeout_hours: 48,
    timeout_behavior: "auto_reject",
  });
  const [skipLlmAdjudication, setSkipLlmAdjudication] = useState(false);

  const [survivorshipRules, setSurvivorshipRules] = useState<
    MdmSurvivorshipRule[]
  >(defaultSurvivorshipRules(canonicalFields));

  const [forceHitlRules, setForceHitlRules] = useState<MdmForceHitlRule[]>([]);

  // Per-field source origin (registered source name, e.g.
  // "oracle:TXCWDEMO/PROVIDER") so the Survivorship grid can show reviewers
  // which DB/table each prioritized column comes from. Best-effort — fields
  // are only discoverable once a run has been normalized.
  const [fieldSources, setFieldSources] = useState<Record<string, string>>({});
  // The ACTUAL fields discovered from the source data. The whole config —
  // grouping keys, matching rules, survivorship — must be built from these, not
  // a hardcoded person schema: keying on fields the source doesn't have (e.g.
  // first_name/last_name on a provider table) yields zero candidate pairs and
  // therefore zero merges. See pickDefaultBlockingKeys.
  const [discoveredFields, setDiscoveredFields] = useState<string[]>([]);
  const seededRef = useRef(false);
  // Set the moment the user touches any config tab, so a late-resolving
  // getFields seed can never clobber their edits (e.g. re-adding a grouping
  // key they just unchecked).
  const userEditedRef = useRef(false);
  const markEdited = <T,>(setter: (v: T) => void) => {
    return (v: T) => {
      userEditedRef.current = true;
      setter(v);
    };
  };
  useEffect(() => {
    let cancelled = false;
    getFields(runId)
      .then((fields) => {
        if (cancelled) return;
        const map: Record<string, string> = {};
        const names: string[] = [];
        for (const f of fields) {
          if (f.canonical_field) {
            names.push(f.canonical_field);
            if (f.source_system) map[f.canonical_field] = f.source_system;
          }
        }
        setFieldSources(map);
        setDiscoveredFields(names);
        // Seed the config defaults from the real fields exactly once, before
        // the user starts editing. There is no saved-config load in this view,
        // so first-load seeding can't clobber user intent.
        if (!seededRef.current && !userEditedRef.current && names.length) {
          seededRef.current = true;
          const defaultKeys = pickDefaultBlockingKeys(names);
          setBlocking({
            strategy: recommendBlockingStrategy(defaultKeys),
            blocking_keys: defaultKeys,
          });
          setMatchRules(defaultMatchRules(names));
          setCompositeRules(defaultCompositeRules(names));
          setSurvivorshipRules(defaultSurvivorshipRules(names));
        }
      })
      .catch(() => {
        // Non-fatal — falls back to the (possibly empty) prop field list.
      });
    return () => {
      cancelled = true;
    };
  }, [runId]);

  // Field list every tab renders from: the discovered source fields when
  // available, else the prop fallback.
  const effectiveFields = discoveredFields.length
    ? discoveredFields
    : canonicalFields;

  // Fetch actual run status from the backend on mount so the guard below
  // is reliable even when the Zustand store hasn't been populated yet
  // (e.g. user navigated directly to this URL).
  useEffect(() => {
    let cancelled = false;
    getRunStatus(runId)
      .then((run) => {
        if (cancelled) return;
        setActualRunStatus(run.status);
        // Sync store so the rest of the layout reflects the real status
        setRun(run.id, run.name);
        setRunStatus(run.status);
        if (run.entity_type) setEntityType(run.entity_type);
      })
      .catch(() => {
        // Non-fatal — store value is used as fallback
      });
    return () => {
      cancelled = true;
    };
  }, [runId, setRun, setRunStatus, setEntityType]);

  // A run is "done" if either the freshly-fetched status OR the store says so.
  const isRunCompleted =
    actualRunStatus === "completed" || runStatus === "completed";

  function safeStr(e: unknown): string {
    try {
      if (typeof e === "string") return e;
      if (e instanceof Error) {
        const msg = e.message;
        // Detect if message itself is "[object Object]" — coercion artifact
        if (msg === "[object Object]") {
          return `Error (coerced): constructor=${e.constructor?.name ?? "unknown"}, name=${e.name}`;
        }
        return msg || e.toString();
      }
      const json = JSON.stringify(e);
      return json === undefined ? String(e) : json;
    } catch {
      return "(unknown error — could not stringify)";
    }
  }

  async function handleTestRun() {
    if (isRunCompleted) {
      setSaveError(
        "This run is already completed. Start a new run to test a different configuration.",
      );
      return;
    }
    setTestLoading(true);
    setSaveError(null);
    setDebugError(null);

    // Step 1: save config
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      await saveMatchConfig({
        run_id: runId,
        project_id: projectId,
        name: configName,
        version: `v${Date.now()}`,
        blocking,
        match_rules: matchRules,
        composite_rules: compositeRules,
        confidence_bands: confidenceBands,
        survivorship_rules: survivorshipRules,
        force_hitl_rules: forceHitlRules,
        skip_llm_adjudication: skipLlmAdjudication,
      } as any);
    } catch (e: unknown) {
      const raw = safeStr(e);
      const debug = `[saveConfig] type=${typeof e} isErr=${e instanceof Error} raw=${raw}`;
      console.error(debug, e);
      setDebugError(debug);
      setSaveError(`Save config failed: ${raw}`);
      setTestLoading(false);
      return;
    }

    // Step 2: if the run is already in-progress (blocking / running / active),
    // reset it first so the backend accepts a fresh execute call.
    const inProgressStatuses = new Set([
      "blocking",
      "running",
      "active",
      "normalizing",
      "clustering",
    ]);
    const currentStatus = actualRunStatus ?? runStatus ?? "";
    if (inProgressStatuses.has(currentStatus)) {
      try {
        await resetRun(runId);
        // Give the backend a moment to update state before execute
        await new Promise((r) => setTimeout(r, 600));
      } catch (e: unknown) {
        const raw = safeStr(e);
        const debug = `[resetRun] type=${typeof e} isErr=${e instanceof Error} raw=${raw}`;
        console.error(debug, e);
        setDebugError(debug);
        setSaveError(`Reset failed before re-run: ${raw}`);
        setTestLoading(false);
        return;
      }
    }

    // Step 3: execute run
    try {
      await executeRun(runId, { sampleSize: 5000 });
    } catch (e: unknown) {
      const raw = safeStr(e);
      const debug = `[executeRun] type=${typeof e} isErr=${e instanceof Error} raw=${raw}`;
      console.error(debug, e);
      setDebugError(debug);
      setSaveError(`Execute failed: ${raw}`);
      setTestLoading(false);
      return;
    }

    setTestLoading(false);
    onTestRun();
  }

  async function handleSave() {
    setSaveLoading(true);
    setSaveError(null);
    setSaveSuccess(false);
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const saved = await saveMatchConfig({
        run_id: runId,
        project_id: projectId,
        name: configName,
        version: `v${Date.now()}`,
        blocking,
        match_rules: matchRules,
        composite_rules: compositeRules,
        confidence_bands: confidenceBands,
        survivorship_rules: survivorshipRules,
        force_hitl_rules: forceHitlRules,
        skip_llm_adjudication: skipLlmAdjudication,
      } as any);
      setVersionHistory((prev) => [
        {
          id: saved.id,
          name: saved.name,
          version: saved.version,
          created_at: saved.created_at,
          run_count: saved.run_count,
        },
        ...prev,
      ]);
      setSaveSuccess(true);
      setShowVersionPanel(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (e: unknown) {
      setSaveError(safeStr(e) || "Save failed — check rule configuration");
    } finally {
      setSaveLoading(false);
    }
  }

  const TABS: { id: TabId; label: string }[] = [
    { id: "blocking", label: "Grouping" },
    { id: "match_rules", label: "Matching Rules" },
    { id: "field_weights", label: "Field Weights" },
    { id: "confidence", label: "Confidence Bands" },
    { id: "survivorship", label: "Survivorship" },
    { id: "force_hitl", label: "Force Manual Review Overrides" },
  ];

  // Entity type from prop or store
  const { entityType: storeEntityType } = useMdmRunStore();
  const activeEntityType = entityTypeProp ?? storeEntityType;

  const [applyingTemplate, setApplyingTemplate] = useState(false);
  const [templateWarning, setTemplateWarning] = useState<string[] | null>(null);
  const [exporting, setExporting] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const importInputRef = useRef<HTMLInputElement>(null);

  return (
    <div className="rule-builder flex h-full flex-col">
      {/* Tab rail */}
      <div className="mdm-tab-rail sticky top-0 z-10 border-b border-[var(--border-muted)] bg-[var(--surface-dark)]/90 backdrop-blur-md">
        <div className="flex items-center gap-0 overflow-x-auto px-6">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              data-testid="mdm-config-tab"
              data-tab={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`relative whitespace-nowrap px-4 py-3.5 text-sm font-medium transition-colors focus:outline-none ${
                activeTab === tab.id
                  ? "text-[#86bc25]"
                  : "text-[var(--text-primary)] hover:text-slate-300"
              }`}
            >
              {tab.label}
              {activeTab === tab.id && (
                <span className="absolute bottom-0 left-0 right-0 h-0.5 rounded-full bg-[#86bc25]" />
              )}
            </button>
          ))}
          {/* Entity type badge */}
          {activeEntityType && (
            <span className="ml-auto mr-2 px-2 py-0.5 rounded-full text-[10px] font-medium border border-indigo-500/30 bg-indigo-500/10 text-indigo-300 uppercase tracking-wide shrink-0">
              {activeEntityType}
            </span>
          )}
          {/* Apply Entity Template */}
          {activeEntityType && (
            <button
              disabled={applyingTemplate}
              onClick={async () => {
                setApplyingTemplate(true);
                setTemplateWarning(null);
                try {
                  const result = await applyEntityTemplate(
                    runId,
                    activeEntityType as EntityType | undefined,
                  );
                  if (result.skipped_fields?.length)
                    setTemplateWarning(result.skipped_fields);
                  // Reload fields to reflect template
                  const newFields = await getFields(runId);
                  setMatchRules(
                    defaultMatchRules(newFields.map((f) => f.canonical_field)),
                  );
                } catch (e) {
                  setSaveError(
                    e instanceof Error ? e.message : "Template apply failed",
                  );
                } finally {
                  setApplyingTemplate(false);
                }
              }}
              className="shrink-0 flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg border border-indigo-400/30 bg-indigo-400/10 text-indigo-300 hover:bg-indigo-400/20 disabled:opacity-40 transition"
            >
              {applyingTemplate ? "Applying…" : "Apply Template"}
            </button>
          )}
          {/* Export Config */}
          <button
            disabled={exporting}
            onClick={async () => {
              setExporting(true);
              try {
                const configs = await import("../api/mdm-client").then((m) =>
                  m.getMatchConfigs(projectId),
                );
                if (configs.length === 0) {
                  setSaveError("No saved configs to export.");
                  return;
                }
                const latest = configs[0] as { id: string };
                const bundle = await exportMatchConfig(latest.id);
                const blob = new Blob([JSON.stringify(bundle, null, 2)], {
                  type: "application/json",
                });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `mdm-config-${latest.id.slice(0, 8)}.json`;
                a.click();
                URL.revokeObjectURL(url);
              } catch (e) {
                setSaveError(e instanceof Error ? e.message : "Export failed");
              } finally {
                setExporting(false);
              }
            }}
            className="shrink-0 flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg border border-[var(--border-muted)] bg-white/[0.04] text-white/60 hover:bg-white/[0.07] disabled:opacity-40 transition"
          >
            Export
          </button>
          {/* Import Config */}
          <>
            <input
              ref={importInputRef}
              type="file"
              accept=".json"
              className="hidden"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                try {
                  const text = await file.text();
                  const payload = JSON.parse(text);
                  const { valid, errors } =
                    await validateImportedConfig(payload);
                  if (!valid) {
                    setImportError(errors.join("; "));
                    return;
                  }
                  // Apply fields from imported config
                  if (payload.fields)
                    setMatchRules(
                      defaultMatchRules(
                        (payload.fields as { field: string }[]).map(
                          (f) => f.field,
                        ),
                      ),
                    );
                  setImportError(null);
                } catch (err) {
                  setImportError(
                    err instanceof Error ? err.message : "Invalid JSON",
                  );
                }
                e.target.value = "";
              }}
            />
            <button
              onClick={() => importInputRef.current?.click()}
              className="shrink-0 flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg border border-[var(--border-muted)] bg-white/[0.04] text-white/60 hover:bg-white/[0.07] transition"
            >
              Import
            </button>
          </>
        </div>
        {/* Advisory banners */}
        {templateWarning && templateWarning.length > 0 && (
          <div className="flex items-start gap-2 px-6 py-2 bg-yellow-500/10 border-t border-yellow-500/20 text-xs text-yellow-300">
            <span className="shrink-0">⚠</span>
            <span>
              Template applied — {templateWarning.length} canonical fields not
              found in sources: {templateWarning.slice(0, 5).join(", ")}
              {templateWarning.length > 5
                ? ` +${templateWarning.length - 5} more`
                : ""}
              .
            </span>
            <button
              onClick={() => setTemplateWarning(null)}
              className="ml-auto text-yellow-400/60 hover:text-yellow-300"
            >
              ✕
            </button>
          </div>
        )}
        {importError && (
          <div className="flex items-center gap-2 px-6 py-2 bg-red-500/10 border-t border-red-500/20 text-xs text-red-300">
            <span>Import validation failed: {importError}</span>
            <button
              onClick={() => setImportError(null)}
              className="ml-auto text-red-400/60 hover:text-red-300"
            >
              ✕
            </button>
          </div>
        )}
      </div>

      {/* Tab content */}
      <div className="min-h-0 flex-1 overflow-y-auto">
        {activeTab === "blocking" && (
          <BlockingTab
            fields={effectiveFields}
            config={blocking}
            onChange={markEdited(setBlocking)}
            runId={runId}
          />
        )}
        {activeTab === "match_rules" && (
          <CompositeRulesTab
            runId={runId}
            fields={effectiveFields}
            rules={compositeRules}
            onChange={markEdited(setCompositeRules)}
          />
        )}
        {activeTab === "field_weights" && (
          <MatchRulesTab
            runId={runId}
            rules={matchRules}
            onChange={markEdited(setMatchRules)}
          />
        )}
        {activeTab === "confidence" && (
          <ConfidenceTab
            bands={confidenceBands}
            onChange={markEdited(setConfidenceBands)}
            skipLlmAdjudication={skipLlmAdjudication}
            onSkipLlmChange={markEdited(setSkipLlmAdjudication)}
          />
        )}
        {activeTab === "survivorship" && (
          <SurvivorshipTab
            rules={survivorshipRules}
            onChange={markEdited(setSurvivorshipRules)}
            fieldSources={fieldSources}
          />
        )}
        {activeTab === "force_hitl" && (
          <ForceHitlTab
            rules={forceHitlRules}
            fields={effectiveFields}
            onChange={markEdited(setForceHitlRules)}
          />
        )}
      </div>

      {/* Bottom action bar */}
      <div className="border-t border-[var(--border-muted)] bg-[var(--surface-dark)]/95 backdrop-blur-md">
        <div className="flex flex-wrap items-center gap-3 px-6 py-3">
          {/* Test run */}
          <Btn
            onClick={handleTestRun}
            disabled={testLoading || isRunCompleted}
            variant="ghost"
            data-testid="mdm-rule-test-run"
          >
            {testLoading ? (
              <>
                <svg
                  className="h-3.5 w-3.5 animate-spin"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v8H4z"
                  />
                </svg>
                Running…
              </>
            ) : isRunCompleted ? (
              <>
                <svg
                  className="h-3.5 w-3.5"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M20 6L9 17l-5-5" />
                </svg>
                Run Completed
              </>
            ) : (
              <>
                <svg
                  className="h-3.5 w-3.5"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <polygon points="5 3 19 12 5 21 5 3" />
                </svg>
                Run Batch
              </>
            )}
          </Btn>

          <div className="mx-2 h-6 w-px bg-[var(--overlay-strong)]" />

          {/* Save config */}
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={configName}
              data-testid="mdm-config-name"
              onChange={(e) => setConfigName(e.target.value)}
              placeholder="Config name"
              className={`${inputCls} w-40`}
            />
            <Btn
              onClick={handleSave}
              disabled={saveLoading || !configName.trim()}
              variant="primary"
              data-testid="mdm-rule-save"
            >
              {saveLoading ? (
                <>
                  <svg
                    className="h-3.5 w-3.5 animate-spin"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v8H4z"
                    />
                  </svg>
                  Saving…
                </>
              ) : (
                <>
                  <svg
                    className="h-3.5 w-3.5"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z" />
                    <polyline points="17 21 17 13 7 13 7 21" />
                    <polyline points="7 3 7 8 15 8" />
                  </svg>
                  Save & Version Config
                </>
              )}
            </Btn>
          </div>

          {/* Save feedback */}
          {saveSuccess && (
            <span
              data-testid="mdm-rule-save-success"
              className="flex items-center gap-1.5 text-xs font-medium"
              style={{ color: "#86bc25" }}
            >
              <svg
                className="h-3.5 w-3.5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
              >
                <path d="M20 6L9 17l-5-5" />
              </svg>
              Saved — run will use this config
            </span>
          )}
          {saveError && (
            <span
              className="flex items-center gap-1.5 text-xs font-medium text-rose-400 max-w-lg"
              title={saveError}
            >
              <svg
                className="h-3.5 w-3.5 shrink-0"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
              >
                <circle cx="12" cy="12" r="10" />
                <path d="M12 8v4M12 16h.01" />
              </svg>
              <span className="break-all">
                {typeof saveError === "string"
                  ? saveError
                  : JSON.stringify(saveError)}
              </span>
            </span>
          )}
          {debugError && (
            <span
              className="text-[11px] font-mono text-[var(--text-primary)] max-w-lg"
              title={debugError}
            >
              dbg: {debugError}
            </span>
          )}

          {/* Version history toggle */}
          {versionHistory.length > 0 && (
            <>
              <div className="mx-2 h-6 w-px bg-[var(--overlay-strong)]" />
              <button
                onClick={() => setShowVersionPanel((v) => !v)}
                className="flex items-center gap-1.5 text-xs text-[var(--text-primary)] transition-colors hover:text-slate-300 focus:outline-none"
              >
                <svg
                  className="h-3.5 w-3.5"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12 6 12 12 16 14" />
                </svg>
                {versionHistory.length} version
                {versionHistory.length !== 1 ? "s" : ""}
              </button>
            </>
          )}
        </div>

        {/* Version history panel */}
        {showVersionPanel && versionHistory.length > 0 && (
          <div className="border-t border-[var(--border-muted)] px-6 py-3">
            <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-[var(--text-primary)]">
              Version History
            </p>
            <div className="flex flex-col gap-1">
              {versionHistory.map((v) => (
                <div
                  key={v.id}
                  className="flex items-center gap-4 text-xs"
                  style={{ fontVariantNumeric: "tabular-nums" }}
                >
                  <span className="font-medium text-slate-300">{v.name}</span>
                  <Badge variant="default">{v.version}</Badge>
                  <span className="text-[var(--text-primary)]">
                    {new Date(v.created_at).toLocaleString()}
                  </span>
                  <span className="text-[var(--text-primary)]">
                    {v.run_count} runs
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default MdmRuleBuilderView;
